import dayjs, { Dayjs } from 'dayjs';
import DOMPurify from 'dompurify';
import type { TOptions } from 'i18next';
import { iconComponents } from '../../components/Core/IconImports';
import { AppEnvModeEnum } from '../../enums/AppEnvModeEnum';
import { EmailLimitationEnum } from '../../enums/EmailLimitationEnum';
import { FileLimitationEnum } from '../../enums/FileLimitationEnum';
import { FilesTableEnum } from '../../enums/FilesTableEnum';
import { FileTypeEnum } from '../../enums/FileTypeEnum';
import { GroupLimitationEnum } from '../../enums/GroupLimitationEnum';
import { InterfaceLimitationEnum } from '../../enums/InterfaceLimitationEnum';
import { SpecialFolderEnum } from '../../enums/SpecialFolderEnum';
import { SpecialFolderTypeEnum } from '../../enums/SpecialFolderTypeEnum';
import { UserLimitationEnum } from '../../enums/UserLimitationEnum';
import { UserRoleEnum } from '../../enums/UserRoleEnum';
import { WebSocketStatusEnum } from '../../enums/WebSocketStatusEnum';
import type EmailLimitation from '../../interfaces/EmailLimitation';
import type File from '../../interfaces/File';
import type FileLimitation from '../../interfaces/FileLimitation';
import type GroupLimitation from '../../interfaces/GroupLimitation';
import type InterfaceLimitation from '../../interfaces/InterfaceLimitation';
import type User from '../../interfaces/User';
import type UserLimitation from '../../interfaces/UserLimitation';
import type { WebSocketStatusDetail } from '../../interfaces/WebSocketStatusDetail';
import { translate } from '../translation';

// Constants used for ticks and date conversion between .NET and JS Date objects
const EPOCH_OFFSET = 621355968000000000;
const TICKS_PER_MS = 10000;

/**
 * Sanitize a string before using it with React `dangerouslySetInnerHTML` (`__html`).
 *
 * Prefer rendering plain text as children when markup is not required (avoids an HTML sink entirely).
 * Use this helper for defense in depth when HTML is intentional (e.g. rich labels from a trusted CMS),
 * so scripts and dangerous attributes are stripped while common safe tags can remain.
 */
export function sanitizeHtmlForInnerHTML(html: string): string {
  if (typeof html !== 'string' || html.length === 0) {
    return '';
  }
  return DOMPurify.sanitize(html);
}

/**
 * Returns the current app environment mode from VITE_APP_ENV_MODE.
 * If not set, empty, or not one of the enum values, returns Production (safe default).
 */
export function getAppEnvMode(): AppEnvModeEnum {
  const raw = (import.meta.env.VITE_APP_ENV_MODE as string | undefined)?.trim();
  if (
    raw === AppEnvModeEnum.Localhost ||
    raw === AppEnvModeEnum.Development ||
    raw === AppEnvModeEnum.Staging ||
    raw === AppEnvModeEnum.Production
  ) {
    return raw as AppEnvModeEnum;
  }
  return AppEnvModeEnum.Production;
}

/**
 * Returns true when the app is running in a development-like environment
 * (localhost or development, based on VITE_APP_ENV_MODE).
 * Safe to call in SSR (returns false when window is undefined).
 */
export function isDevEnvironment(): boolean {
  if (typeof window === 'undefined') return false;
  const env = getAppEnvMode();
  return env === AppEnvModeEnum.Localhost || env === AppEnvModeEnum.Development;
}

/**
 * Formats a file size in bytes into a human readable string (B, KB, MB).
 * @param fileSizeInBytes - Size in bytes.
 * @returns Human readable representation.
 */
export function formatFileSize(fileSizeInBytes: number = 0): string {
  if (fileSizeInBytes < 1024) {
    return `${fileSizeInBytes} B`;
  }
  if (fileSizeInBytes < 1024 * 1024) {
    return `${(fileSizeInBytes / 1024).toFixed(1)} KB`;
  }
  return `${(fileSizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Formats a duration in seconds as MM:SS or HH:MM:SS (reusable for countdowns, timers, etc.).
 * @param seconds - Total remaining seconds to format
 * @returns Formatted string in MM:SS or HH:MM:SS
 */
export function formatRemainingTimeFromSeconds(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Extracts the file extension from a fileName (e.g. "50mb.pdf" → "pdf", "file.name.md" → "md").
 * Returns empty string if there is no dot (e.g. folder names or extensionless files).
 * @param fileName - Full file or folder name.
 * @returns The extension (no leading dot) or empty string.
 */
export function getExtensionFromFileName(fileName: string): string {
  if (!fileName) return '';
  const lastDot = fileName.lastIndexOf('.');
  return lastDot >= 0 ? fileName.slice(lastDot + 1) : '';
}

/** Markdown file extensions opened with the WYSIWYG note editor (legacy notes — rendered as markdown). */
export const MARKDOWN_FILE_EXTENSIONS = ['.md', '.markdown', '.mkd', '.mkdn', '.mkdown'];

/** Extension for HTML notes created by the WYSIWYG note editor. Content is sanitized HTML, not markdown. */
export const NOTE_FILE_EXTENSION = '.fgnote';

/** Normalize an extension to a leading-dot, lower-case form (e.g. "MD" → ".md"). */
function normalizeExtension(fileExtension: string): string {
  return fileExtension.startsWith('.')
    ? fileExtension.toLowerCase()
    : `.${fileExtension.toLowerCase()}`;
}

/**
 * True when the given file extension is a markdown note extension.
 * Accepts values with or without a leading dot (e.g. "md" or ".md").
 */
export function isMarkdownExtension(fileExtension: string | undefined): boolean {
  if (!fileExtension) return false;
  return MARKDOWN_FILE_EXTENSIONS.includes(normalizeExtension(fileExtension));
}

/** True for the HTML note extension (`.fgnote`). Content is sanitized HTML. */
export function isHtmlNoteExtension(fileExtension: string | undefined): boolean {
  if (!fileExtension) return false;
  return normalizeExtension(fileExtension) === NOTE_FILE_EXTENSION;
}

/**
 * True when the file opens in the WYSIWYG note editor: either an HTML note (`.fgnote`)
 * or a legacy markdown note (`.md` and friends).
 */
export function isNoteExtension(fileExtension: string | undefined): boolean {
  return isHtmlNoteExtension(fileExtension) || isMarkdownExtension(fileExtension);
}

/** True for the `.fgsecret` extension (backend may still report Unknown/Text). */
export function isSecretExtension(fileExtension: string | undefined): boolean {
  if (!fileExtension) return false;
  return normalizeExtension(fileExtension) === SECRET_FILE_EXTENSION;
}

/**
 * True when a list/detail file should use the secret modal/viewer path.
 * Prefer `fileType`, fall back to extension / file name (API often returns Unknown).
 */
export function isSecretFile(file: Pick<File, 'fileType' | 'fileExtension' | 'fileName'>): boolean {
  if (file.fileType === FileTypeEnum.Secret) return true;
  if (isSecretExtension(file.fileExtension)) return true;
  return (file.fileName ?? '').toLowerCase().endsWith(SECRET_FILE_EXTENSION);
}

/**
 * Builds the public file share URL for a given public token.
 * Returns empty string if window is not available (e.g. server-side rendering).
 * @param publicToken - The public share token.
 * @returns Full URL to open the public share (e.g. https://example.com/files/public/abc123).
 */
export function getFilePublicLink(publicToken: string): string {
  if (typeof window === 'undefined' || !publicToken) return '';
  return `${window.location.origin}/files/public/${publicToken}`;
}

/**
 * Convert a JavaScript Date to .NET ticks.
 * @param {Date} date - The date to convert.
 * @returns {number} - The equivalent ticks.
 */
export function dateToTicks(date: Date): number {
  return date.getTime() * TICKS_PER_MS + EPOCH_OFFSET;
}

/**
 * Convert .NET ticks to a JavaScript Date.
 * @param {number} ticks - The ticks to convert.
 * @returns {Date} - The resulting JavaScript Date.
 */
export function dateFromTicks(ticks: number): Date {
  const ms = (ticks - EPOCH_OFFSET) / TICKS_PER_MS;
  return new Date(ms);
}

/**
 * Convert .NET ticks to milliseconds since Unix epoch.
 * @param {number} ticks - The ticks to convert.
 * @returns {number} - The milliseconds since Unix epoch.
 */
export function ticksToMs(ticks: number): number {
  return (ticks - EPOCH_OFFSET) / TICKS_PER_MS;
}

/**
 * Convert .NET ticks to a Dayjs date object (UTC).
 * @param {number} ticks - The ticks to convert.
 * @returns {Dayjs} - The Dayjs object representing the date/time in UTC.
 */
export function ticksToDayjs(ticks: number): Dayjs {
  return dayjs.utc(ticksToMs(ticks));
}

/**
 * Convert milliseconds to .NET ticks.
 * @param {number} ms - The milliseconds to convert.
 * @returns {number} - The ticks.
 */
export function msToTicks(ms: number): number {
  return ms * TICKS_PER_MS + EPOCH_OFFSET;
}

/**
 * Convert UTC .NET ticks to a Dayjs object as a local date (ignores time).
 * @param {number} ticks - UTC ticks.
 * @returns {Dayjs} - Local Dayjs object with year, month, day.
 */
export function localDateFromUtcTicks(ticks: number): Dayjs {
  const msUtc = ticksToMs(ticks);
  const dtUtc = dayjs.utc(msUtc);
  // Dayjs months are 0-indexed, so adding 1
  const year = dtUtc.year();
  const month = dtUtc.month() + 1;
  const day = dtUtc.date();
  return dayjs(`${year}-${month}-${day}`, 'YYYY-M-D');
}

/**
 * Returns a high-contrast color (white or dark) for icons/text on a given background.
 * Uses WCAG relative luminance: dark backgrounds get white, light backgrounds get dark gray.
 *
 * @param backgroundColorHex - Header/background color in hex (e.g. '#1e3a5f' or '#fff').
 * @returns CSS color string for the foreground (e.g. '#ffffff' or '#1f2937').
 */
export function getContrastColorForBackground(backgroundColorHex: string): string {
  const hex = backgroundColorHex.replace(/^#/, '');
  if (hex.length !== 3 && hex.length !== 6) {
    return '#ffffff';
  }
  let r: number, g: number, b: number;
  if (hex.length === 3) {
    r = parseInt(hex[0]! + hex[0], 16) / 255;
    g = parseInt(hex[1]! + hex[1], 16) / 255;
    b = parseInt(hex[2]! + hex[2], 16) / 255;
  } else {
    r = parseInt(hex.slice(0, 2), 16) / 255;
    g = parseInt(hex.slice(2, 4), 16) / 255;
    b = parseInt(hex.slice(4, 6), 16) / 255;
  }
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.4 ? '#1f2937' : '#ffffff';
}

/**
 * Get a HEX color representing whether the user is expired or not.
 * @param {boolean} isExpired - Whether the user is expired.
 * @returns {string} - Associated color code.
 */
export function getUserStatusColor(isExpired: boolean): string {
  return !isExpired ? '#008036' : '#AB0D02';
}

/**
 * Get user status label for a given user: blocked > expired > active.
 * @param {User} user - The user object.
 * @returns {string} - Translated user status label.
 */
export function getUserStatusText(user: User): string {
  if (user.isBlocked) {
    return translate('blocked');
  } else if (user.isExpired) {
    return translate('expired');
  } else {
    return translate('active');
  }
}

/**
 * Get the icon name for a given user role enum.
 * @param {UserRoleEnum} role - The user role.
 * @returns {keyof typeof iconComponents} - Corresponding icon name.
 */
export function getUserRoleIconName(role?: UserRoleEnum): keyof typeof iconComponents {
  switch (role) {
    case UserRoleEnum.SuperAdmin:
      return 'CrownIcon';
    case UserRoleEnum.Admin:
      return 'UserStarIcon';
    case UserRoleEnum.Contributor:
      return 'UserPenIcon';
    case UserRoleEnum.Reader:
      return 'UserIcon';
    default:
      return 'AtSignIcon';
  }
}

/**
 * Whether the 2FA activation promo overlay may be shown for this role.
 * SuperAdmin, Admin, and Contributor only; Reader and External never.
 */
export function isTotpPromoEligibleRole(role?: UserRoleEnum): boolean {
  if (role === undefined) return false;
  return (
    role === UserRoleEnum.SuperAdmin ||
    role === UserRoleEnum.Admin ||
    role === UserRoleEnum.Contributor
  );
}

/**
 * Convert an ArrayBuffer or byte view to a base64-encoded string.
 * @param {ArrayBuffer | Uint8Array} buffer - The data buffer.
 * @returns {string} - Base64 encoded string.
 */
export function arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
  let binary = '';
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return window.btoa(binary);
}

/**
 * Generate a SHA-256 hash of the given bytes (ArrayBuffer or Uint8Array).
 * Optionally returns hex or base64 encoding.
 * @param {ArrayBuffer | Uint8Array} fileBytes - The file data.
 * @param {boolean} formatHex - If true, returns hex; else, base64.
 * @returns {Promise<string>} - Hash of the input as a string.
 */
export async function getHashFromBytes(
  fileBytes: ArrayBuffer | Uint8Array,
  formatHex: boolean,
): Promise<string> {
  const buffer = await crypto.subtle.digest('SHA-256', fileBytes as ArrayBuffer);
  if (formatHex) {
    // Convert hash buffer to hex
    const hashArray = Array.from(new Uint8Array(buffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  } else {
    // Convert to base64
    return arrayBufferToBase64(buffer);
  }
}

/**
 * Generate a unique SHA-256 hash of the company's origin URL (window.location.origin as salt).
 * @returns {Promise<string>} - Hash string (hex-encoded).
 */
export async function getCompanyHash(): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(window.location.origin);
  return await getHashFromBytes(data, true);
}

export { generateUUID } from './generateUUID';

/**
 * Get the base URL for editing/reading an office file given its type and mode.
 * @param {FileTypeEnum} fileType - The file type (Word, Excel, Powerpoint, Wopitest).
 * @param {boolean} [readOnly=false] - If true, returns a read-only URL.
 * @returns {string} - Office service URL.
 */
export function getOfficeFileBaseUrl(fileType: FileTypeEnum, readOnly: boolean = false): string {
  switch (fileType) {
    case FileTypeEnum.Word:
      return readOnly
        ? `${import.meta.env.VITE_APP_OFFICE_VIEW_WORD_URL}/wv/wordviewerframe.aspx?`
        : `${import.meta.env.VITE_APP_OFFICE_WORD_URL}/we/wordeditorframe.aspx?`;
    case FileTypeEnum.Excel:
      return `${import.meta.env.VITE_APP_OFFICE_EXCEL_URL}/x/_layouts/xlviewerinternal.aspx?edit=${readOnly ? 0 : 1}&`;
    case FileTypeEnum.Powerpoint:
      return `${import.meta.env.VITE_APP_OFFICE_POWERPOINT_URL}/p/PowerPointFrame.aspx?PowerPointView=EditView&`;
    case FileTypeEnum.Wopitest:
      return `${import.meta.env.VITE_APP_OFFICE_WOPITEST_URL}/hosting/WopiTestFrame.aspx?`;
    default:
      return '';
  }
}

/**
 * Get favicon URL for a file type.
 * @param {FileTypeEnum} fileType - The office file type.
 * @returns {string} - URL for the corresponding file type favicon.
 */
export function getOfficeFileFavicon(fileType: FileTypeEnum): string {
  switch (fileType) {
    case FileTypeEnum.Word:
      return import.meta.env.VITE_APP_OFFICE_WORD_ICON_URL;
    case FileTypeEnum.Excel:
      return import.meta.env.VITE_APP_OFFICE_EXCEL_ICON_URL;
    case FileTypeEnum.Powerpoint:
      return import.meta.env.VITE_APP_OFFICE_POWERPOINT_ICON_URL;
    case FileTypeEnum.Wopitest:
      return import.meta.env.VITE_APP_OFFICE_WOPITEST;
    default:
      return '';
  }
}

/**
 * Turn a string into a URL-friendly slug.
 * @param {string} text - Text to slugify.
 * @returns {string} - Slugified value.
 */
export function slugify(text: string): string {
  return text
    .toString()
    .normalize('NFD') // Decompose diacritics
    .replace(/[\u0300-\u036f]/g, '') // Strip diacritics
    .toLowerCase()
    .replace(/\s+/g, '-') // Spaces to hyphens
    .replace(/[_\-]+/g, '-') // Multiple underscores/hyphens to one
    .replace(/[^\w-]+/g, '') // Remove non-word/hyphen chars
    .replace(/--+/g, '-') // Multiple hyphens to one
    .replace(/^-+/, '') // Trim leading hyphens
    .replace(/-+$/, ''); // Trim trailing hyphens
}

/**
 * Copy a string to the system clipboard.
 * Uses modern clipboard API if present, otherwise falls back to execCommand.
 * @param {string} text - The text to copy.
 * @returns {Promise<void>} - Resolves if successful, rejects on error.
 */
export function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  } else {
    // Fallback for older browsers
    return new Promise((resolve, reject) => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      // Prevent page scrolling and styling impact
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        if (successful) {
          resolve();
        } else {
          reject(new Error('Fallback clipboard copy failed.'));
        }
      } catch (err) {
        reject(err);
      } finally {
        document.body.removeChild(textArea);
      }
    });
  }
}

/**
 * Get a HEX color for a folder depending on whether it's QuickShare or not.
 * @param {File} file - File object that may have the isQuickShare property.
 * @returns {string} - HEX color string.
 */
export function getFolderHex(file: File): string {
  if (file?.specialFolder === SpecialFolderTypeEnum.AiGrant) {
    return '#6d28d9';
  }
  if (file?.specialFolder === SpecialFolderTypeEnum.QuickShare) {
    return '#193057';
  }

  if (file?.specialFolder === SpecialFolderTypeEnum.LocalFilesForCloudSharing) {
    return '#1E88E5';
  }

  return '#FFB900';
}

/**
 * Check if the given file has a fileName matching any SpecialFolderEnum value.
 * @param {File} file - File object.
 * @returns {boolean} - True if file is a special folder.
 */
/**
 * Checks if a file extension is supported for thumbnail generation.
 * Supported formats:
 * - Word: .doc, .docx, .docm, .dotx, .dotm, .rtf, .odt, .ott
 * - Excel: .xls, .xlsx, .xlsm, .xlsb, .xltx, .xltm, .csv, .tsv, .ods, .ots
 * - PowerPoint: .ppt, .pptx, .pptm, .potx, .potm, .odp, .otp
 * - PDF: .pdf
 * - PSD: .psd
 * - CAD: .dwg
 * - Images: .jpeg, .jpg, .png, .svg
 *
 * @param extension - File extension with dot (e.g., ".pdf", ".jpeg")
 * @returns true if the extension is supported for thumbnails
 */
export function isFileThumbnailSupported(extension: string): boolean {
  if (!extension || typeof extension !== 'string') {
    return false;
  }

  const normalized = extension.startsWith('.')
    ? extension.toLowerCase()
    : `.${extension.toLowerCase()}`;
  return THUMBNAIL_SUPPORTED_EXTENSIONS.includes(normalized);
}

/** Extensions supported for thumbnail generation (with dot prefix). Used by isFileThumbnailSupported. */
export const THUMBNAIL_SUPPORTED_EXTENSIONS = [
  // Word (+ macro/template OOXML, ODF template)
  '.doc',
  '.docx',
  '.docm',
  '.dotx',
  '.dotm',
  '.rtf',
  '.odt',
  '.ott',
  // Excel (+ macro/template/binary, ODF, tsv)
  '.xls',
  '.xlsx',
  '.xlsm',
  '.xlsb',
  '.xltx',
  '.xltm',
  '.csv',
  '.tsv',
  '.ods',
  '.ots',
  // PowerPoint (+ macro/template OOXML, ODF)
  '.ppt',
  '.pptx',
  '.pptm',
  '.potx',
  '.potm',
  '.odp',
  '.otp',
  // PDF
  '.pdf',
  // PSD
  '.psd',
  // CAD
  '.dwg',
  // Images
  '.jpeg',
  '.jpg',
  '.png',
  '.svg',
];

/** Image-only extensions for thumbnail preview (e.g. in table cells where other types would be too small). */
export const FILE_PREVIEW_EXTENSIONS_IMAGES = ['.jpeg', '.jpg', '.png', '.svg'];

export function isImageFileExtension(fileExtension: string): boolean {
  if (!fileExtension) return false;
  const normalized = fileExtension.startsWith('.')
    ? fileExtension.toLowerCase()
    : `.${fileExtension.toLowerCase()}`;
  return FILE_PREVIEW_EXTENSIONS_IMAGES.includes(normalized);
}

export function areAllListedItemsImages(files: File[]): boolean {
  if (files.length === 0) return false;
  return files.every((file) => !file.isFolder && isImageFileExtension(file.fileExtension));
}

export function isFileSpecialFolder(file: File): boolean {
  if (!file || !file.fileName) return false;
  return Object.values(SpecialFolderEnum).includes(file.fileName as SpecialFolderEnum);
}

/**
 * Get the display title for a file. If it's a special folder, the title is translated.
 * @param {File} file - File object.
 * @returns {string} - The translated title or the file name, or 'N/D' if undefined.
 */
export function getFileTitle(file: File): string {
  if (
    file?.fileName &&
    Object.values(SpecialFolderEnum).includes(file.fileName as SpecialFolderEnum)
  ) {
    return translate(`specialFolders.${file.fileName}`);
  } else {
    return file?.fileName || 'N/D';
  }
}

/**
 * Transforms an absolute storage URL for a branding image to a backend asset proxy URL.
 * Storage URLs follow the pattern: https://.../enterprise-resources/{HASH}/images/{filename}
 * They are converted to: {API_BASE_URL}/assets/images/{filename}
 *
 * If the URL does not match the storage pattern it is returned unchanged.
 */
export function transformBrandingImageUrl(url: string): string {
  if (!url) return url;
  const hashPattern = /([A-Fa-f0-9]{64})\/(.*)/;
  const match = url.match(hashPattern);
  if (match) {
    const proxyBase = `${import.meta.env.VITE_APP_API_BASE_URL}/assets`;
    return `${proxyBase}/${match[2]}`;
  }
  return url;
}

/** Legacy seed values for login email label — treat as unset so i18n (Email/Username) wins. */
const LEGACY_LOGIN_EMAIL_LABELS = new Set(['email', 'email address']);

/** Legacy seed values for login email placeholder — treat as unset so i18n wins. */
const LEGACY_LOGIN_EMAIL_PLACEHOLDERS = new Set(['enter your email address here...']);

/**
 * Prefer branding copy when custom; fall back to i18n when empty or legacy email-only seed.
 */
export function resolveBrandingLoginText(
  brandingValue: string | null | undefined,
  i18nFallback: string,
  legacyDefaults: Set<string>,
): string {
  const trimmed = brandingValue?.trim();
  if (!trimmed || legacyDefaults.has(trimmed.toLowerCase())) {
    return i18nFallback;
  }
  return trimmed;
}

export function resolveLoginPageEmailLabel(
  brandingValue: string | null | undefined,
  i18nFallback: string,
): string {
  return resolveBrandingLoginText(brandingValue, i18nFallback, LEGACY_LOGIN_EMAIL_LABELS);
}

export function resolveLoginPageEmailPlaceholder(
  brandingValue: string | null | undefined,
  i18nFallback: string,
): string {
  return resolveBrandingLoginText(brandingValue, i18nFallback, LEGACY_LOGIN_EMAIL_PLACEHOLDERS);
}

/**
 * Get a default image URL for branding contexts.
 * Uses the backend asset proxy endpoint which serves from storage with local fallback.
 * @param {'email'|'quick-share'|'login-page'|'top-bar'} image - Image context.
 * @returns {string} - URL of the default branding image via proxy endpoint.
 */
export function getBrandingDataDefaultImage(
  image: 'email' | 'quick-share' | 'login-page' | 'top-bar',
): string {
  const base = `${import.meta.env.VITE_APP_API_BASE_URL}/assets`;
  switch (image) {
    case 'email':
      return `${base}/emailNotificationLogo.png?v=${Date.now()}`;
    case 'quick-share':
      return `${base}/quickShare.webp?v=${Date.now()}`;
    case 'login-page':
      return `${base}/loginPageLogo.svg?v=${Date.now()}`;
    case 'top-bar':
      return `${base}/topBarLogo.svg?v=${Date.now()}`;
    default:
      return '';
  }
}

/**
 * Top bar logo URL with cache-bust — same source as {@link Header} (`useHeader` / branding).
 */
export function getTopBarLogoSrc(
  brandingData: { hash?: string; topBarLogo?: string } | null | undefined,
): string {
  const baseUrl = brandingData?.topBarLogo
    ? brandingData.topBarLogo
    : getBrandingDataDefaultImage('top-bar');
  if (!baseUrl) return '';
  const separator = baseUrl.includes('?') ? '&' : '?';
  const version = brandingData?.hash ?? '';
  return version ? `${baseUrl}${separator}v=${version}` : baseUrl;
}

/**
 * Remove `.shortcut` file extension from a given string, if present.
 * @param {string} title - Input file name.
 * @returns {string} - File name without the `.shortcut` extension.
 */
export function removeShortcutExtension(title: string): string {
  const extensionIndex = title.lastIndexOf('.shortcut');
  if (extensionIndex !== -1) {
    title = title.substring(0, extensionIndex);
  }
  return title;
}

/** Extension for password-protected secret files (hidden in the UI). */
export const SECRET_FILE_EXTENSION = '.fgsecret';

/**
 * Remove the .fgsecret extension from a file title if present.
 * @param {string} title - The file title to process.
 * @returns {string} - Title without the .fgsecret extension.
 */
export function removeFileSecretExtension(title: string): string {
  const extensionIndex = title.lastIndexOf(SECRET_FILE_EXTENSION);
  if (extensionIndex !== -1) {
    title = title.substring(0, extensionIndex);
  }
  return title;
}

/**
 * Remove the `.fgnote` extension from a file title if present.
 * @param title - The file title to process.
 * @returns Title without the `.fgnote` extension.
 */
export function removeFileNoteExtension(title: string): string {
  const extensionIndex = title.lastIndexOf(NOTE_FILE_EXTENSION);
  if (extensionIndex !== -1) {
    title = title.substring(0, extensionIndex);
  }
  return title;
}

/**
 * Strips internal Filegrant extensions (`.fgsecret`, `.fgnote`) from display titles.
 */
export function removeInternalFileExtension(title: string): string {
  return removeFileNoteExtension(removeFileSecretExtension(title));
}

/**
 * Validate if a string is a well-formed email address.
 * @param {string} email - Email address string.
 * @returns {boolean} - True if valid email format, else false.
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Get a purchase link for a given file id (store deep link).
 * @param {number} fileId - File identifier.
 * @returns {string} - Purchase/store link.
 */
export function getPurchaseLink(fileId: number): string {
  return document.location.origin + '/store/' + fileId;
}

/**
 * Print symbol for a currency code.
 * @param {string} currency - Currency code.
 * @returns {string} - Currency symbol.
 */
export function printCurrency(currency: string): string {
  switch (currency) {
    case 'EUR':
      return '€';
    case 'USD':
      return '$';
    default:
      return currency;
  }
}

/**
 * Format amount as a string, optionally with currency and locale.
 * @param {number} amount - The number to format.
 * @param {Object} options - Optional: locale and currency.
 * @returns {string} - Formatted number string.
 */
export function printAmount(
  amount: number,
  options?: {
    locale?: string;
    currency?: string;
  },
): string {
  const locale = options?.locale || navigator.language;
  const currency = options?.currency;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...(currency && { style: 'currency', currency }),
  }).format(amount);
}

/**
 * Format a date (ISO string or .NET ticks) according to the given locale.
 * Uses app locale when provided (e.g. from useAppStore), otherwise browser default.
 * @param dateInput - ISO 8601 string (e.g. "2026-03-06T12:00:00Z") or .NET ticks.
 * @param locale - Optional BCP 47 locale (e.g. 'it', 'en'); when null/undefined uses browser default.
 * @returns Localized date string (e.g. DD/MM/YYYY for 'it', MM/DD/YYYY for 'en-US').
 */
export function formatDate(dateInput: string | number, locale?: string | null): string {
  const date = typeof dateInput === 'number' ? new Date(ticksToMs(dateInput)) : new Date(dateInput);
  return date.toLocaleDateString(locale ?? undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Check for a file limitation of a certain type.
 * @param {FileLimitation[]} fileLimitations - Array of file limitation objects.
 * @param {FileLimitationEnum} type - Limitation type to check.
 * @returns {boolean} - True if limitation found.
 */
export function hasFileLimitation(
  fileLimitations: FileLimitation[] | undefined | null,
  type: FileLimitationEnum,
): boolean {
  return (fileLimitations ?? []).some((limitation) => limitation.name === type);
}

/**
 * Whether favorite actions (add/remove) may be shown for a file in the current view.
 * Excludes Trash, Hidden routes and Quick Share special folders.
 */
export function isFileFavoritesActionsAvailable(
  file: Pick<File, 'specialFolder'>,
  isInFileRoute: (route: FilesTableEnum) => boolean,
): boolean {
  return (
    !isInFileRoute(FilesTableEnum.Trash) &&
    !isInFileRoute(FilesTableEnum.Hidden) &&
    file.specialFolder !== SpecialFolderTypeEnum.QuickShare
  );
}

/**
 * Check for a user limitation of a certain type.
 * @param {UserLimitation[]} userLimitations - Array of user limitation objects.
 * @param {UserLimitationEnum} type - Limitation type to check.
 * @returns {boolean} - True if limitation found.
 */
export function hasUserLimitation(
  userLimitations: UserLimitation[],
  type: UserLimitationEnum,
): boolean {
  return userLimitations.some((limitation) => limitation.name === type);
}

/**
 * Check for a group limitation of a certain type.
 * @param {GroupLimitation[]} groupLimitations - Array of group limitation objects.
 * @param {GroupLimitationEnum} type - Limitation type to check.
 * @returns {boolean} - True if limitation found.
 */
export function hasGroupLimitation(
  groupLimitations: GroupLimitation[],
  type: GroupLimitationEnum,
): boolean {
  return groupLimitations.some((limitation) => limitation.name === type);
}

/**
 * Check for an interface limitation of a certain type.
 * @param {InterfaceLimitation[]} interfaceLimitations - Array of interface limitations.
 * @param {InterfaceLimitationEnum} type - Limitation type.
 * @returns {boolean} - True if limitation found.
 */
export function hasInterfaceLimitation(
  interfaceLimitations: InterfaceLimitation[],
  type: InterfaceLimitationEnum,
): boolean {
  return interfaceLimitations.some((limitation) => limitation.name === type);
}

/**
 * Check for an email limitation of a certain type.
 * @param {EmailLimitation[]} emailLimitations - Array of email limitation objects.
 * @param {EmailLimitationEnum} type - Limitation type to check.
 * @returns {boolean} - True if limitation found.
 */
export function hasEmailLimitation(
  emailLimitations: EmailLimitation[],
  type: EmailLimitationEnum,
): boolean {
  return emailLimitations.some((limitation) => limitation.name === type);
}

/**
 * Check if a setting is denied (present in deniedSettings array).
 * @param {string[] | null | undefined} deniedSettings - Array of denied setting enum strings.
 * @param {string} type - Setting type enum to check.
 * @returns {boolean} - True if setting is denied.
 */
export function hasSettingLimitation(
  deniedSettings: string[] | null | undefined,
  type: string,
): boolean {
  if (!deniedSettings || deniedSettings.length === 0) {
    return false;
  }
  return deniedSettings.includes(type);
}

/**
 * Set a deeply nested value in an object using a dot-separated string path.
 * Returns a new object with the change.
 * @param {T} obj - The base object.
 * @param {string} path - Dot-separated path (e.g., "a.b.c").
 * @param {any} value - Value to set.
 * @returns {T} - Updated object.
 */
export function setObjectNestedValue<T extends object>(obj: T, path: string, value: any): T {
  const keys = path.split('.');
  const newObj = { ...obj } as any;
  let curr = newObj;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    if (k === undefined) continue;
    curr[k] = curr[k] ? { ...curr[k] } : {};
    curr = curr[k];
  }
  const lastKey = keys[keys.length - 1];
  if (lastKey !== undefined) {
    curr[lastKey] = value;
  }
  return newObj as T;
}

/**
 * Check if date ticks are before now (UTC).
 * @param {number|null|undefined} ticks - .NET UTC ticks.
 * @returns {boolean} - True if in the past, false otherwise.
 */
export function isTicksBeforeNow(ticks?: number | null): boolean {
  if (!ticks || ticks <= 0) return false;
  return ticksToMs(ticks) < Date.now();
}

/**
 * Check if a folder password session is expired.
 * @param {number} expiresAt - The expiration time in .NET ticks.
 * @param {number} marginMinutes - Optional margin in minutes before considering expired (default: 1).
 * @returns {boolean} - True if expired, false otherwise.
 */
export function isFolderPasswordSessionExpired(
  expiresAt: number,
  marginMinutes: number = 1,
): boolean {
  if (!expiresAt || expiresAt <= 0) return true;
  const now = Date.now();
  const expiresAtMs = ticksToMs(expiresAt);
  const marginMs = marginMinutes * 60 * 1000;
  return expiresAtMs < now + marginMs;
}

/**
 * Return a string representing the time since the given date, e.g., "3 days ago".
 * @param {Date|number} date - The date or timestamp to compare to now.
 * @param {TOptions} locale - Translation options.
 * @returns {string} - Relative time string with translation.
 */
export function timeSince(date: Date | number, locale: TOptions): string {
  const now = new Date();
  const givenDate = typeof date === 'number' ? new Date(date) : date;
  const seconds = Math.floor((now.getTime() - givenDate.getTime()) / 1000);

  let interval = seconds / 31536000;
  if (interval > 1) {
    return Math.floor(interval) + ' ' + translate('common.yearsAgo', locale);
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return Math.floor(interval) + ' ' + translate('common.monthsAgo', locale);
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return Math.floor(interval) + ' ' + translate('common.daysAgo', locale);
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return Math.floor(interval) + ' ' + translate('common.hoursAgo', locale);
  }
  interval = seconds / 60;
  if (interval > 1) {
    return Math.floor(interval) + ' ' + translate('common.minutesAgo', locale);
  }
  return Math.floor(seconds + 1) + ' ' + translate('common.secondsAgo', locale);
}

/**
 * Format byte count into human-readable form (bytes, KB, MB, GB).
 * @param {number} bytes - Number of bytes.
 * @returns {string} - String with units.
 */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${bytes} B`;
}

/**
 * Lighten a hex color code by a percentage (default 10%).
 * @param {string} hex - Hex code (e.g., "#aabbcc" or "#abc").
 * @param {number} [percent=10] - Percentage to lighten.
 * @returns {string} - Lightened hex color.
 */
export function lightenHex(hex: string, percent = 10): string {
  // Expand shorthand hex if needed (e.g., #abc => #aabbcc)
  let color = hex.replace(/^#/, '');
  if (color.length === 3) {
    color = color
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const num = parseInt(color, 16);

  let r = (num >> 16) + Math.round(2.55 * percent);
  let g = ((num >> 8) & 0x00ff) + Math.round(2.55 * percent);
  let b = (num & 0x0000ff) + Math.round(2.55 * percent);

  r = Math.max(Math.min(255, r), 0);
  g = Math.max(Math.min(255, g), 0);
  b = Math.max(Math.min(255, b), 0);

  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * Get color and status for a WebSocket connection state.
 * @param {WebSocketStatusEnum} status - WebSocket status enum.
 * @returns {WebSocketStatusDetail} - Color, label, and antd status for display.
 */
export function getWebSocketStatusColor(status: WebSocketStatusEnum): WebSocketStatusDetail {
  switch (status) {
    case WebSocketStatusEnum.Idle:
      return { color: '#9ca3af', label: 'idle', antdStatus: 'default' }; // gray-400
    case WebSocketStatusEnum.Connecting:
      return { color: '#fbbf24', label: 'connecting', antdStatus: 'processing' }; // amber-400
    case WebSocketStatusEnum.Reconnecting:
      return { color: '#fbbf24', label: 'reconnecting', antdStatus: 'processing' }; // amber-400
    case WebSocketStatusEnum.Open:
      return { color: '#10b981', label: 'open', antdStatus: 'success' }; // green-500
    case WebSocketStatusEnum.Closing:
      return { color: '#f59e0b', label: 'closing', antdStatus: 'processing' }; // amber-500
    case WebSocketStatusEnum.Closed:
      return { color: '#6b7280', label: 'closed', antdStatus: 'default' }; // gray-500
    case WebSocketStatusEnum.Error:
      return { color: '#ef4444', label: 'error', antdStatus: 'error' }; // red-500
    default:
      return { color: '#9ca3af', label: 'idle', antdStatus: 'default' }; // fallback
  }
}

/**
 * Get user initials from firstName, lastName, or email.
 * Returns a string with initials (max 2 characters for firstName+lastName, 1 for email).
 * @param {string} [firstName] - User's first name.
 * @param {string} [lastName] - User's last name.
 * @param {string} [email] - User's email address.
 * @returns {string} - Initials string (e.g., "JD" for John Doe, "J" for John, "E" for email@example.com).
 */
export function getUserInitials(firstName?: string, lastName?: string, email?: string): string {
  if (firstName && lastName) {
    return firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (email) {
    // Use first character of email (before @)
    const emailPrefix = email.split('@')[0];
    if (emailPrefix) {
      return emailPrefix.charAt(0).toUpperCase();
    }
    // Fallback: use first character of email if no @ found
    return email.charAt(0).toUpperCase();
  }
  return '';
}

/**
 * Get user display name from firstName, lastName, or email.
 * Returns the best available name representation for display purposes.
 * @param {string} [firstName] - User's first name.
 * @param {string} [lastName] - User's last name.
 * @param {string} [email] - User's email address.
 * @returns {string} - Display name (e.g., "John Doe", "John", or "email@example.com").
 */
export function getUserName(firstName?: string, lastName?: string, email?: string): string {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  if (firstName) {
    return firstName;
  }
  if (email) {
    return email;
  }
  return '';
}
